// =============================================================================
// src/features/explore/explore-tse-zone-card.ts — HTML do popup de uma zona
//   eleitoral do TSE (camada "Zonas eleitorais", pedido 2026-10-13).
//
// Conteudo 100% de dado oficial do asset gerado: eleitorado e locais (TSE 2024),
//   Prefeito 2024 por municipio da zona e Governador 2022 (1o turno). Reusa as
//   classes do popup de ranking (exp-vp-*, explore-vote.css) — mesmo balao.
//   Nomes de candidato/municipio vem de CSV externo -> SEMPRE escapados.
//
// SRP: so geracao de string HTML. Sem DOM, sem Leaflet.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import { bubbleColorOf } from './explore-vote-data';
import { formatVotos, formatPercent } from './explore-vote-anim';
import type { TseZoneCandidato, TseZoneProperties, TseZoneRanking, TseZoneAreaMethod } from './explore-tse-zone-types';

/** Nota de proveniencia da AREA por metodo — a area de Manaus e aproximada. */
const TSE_ZONE_AREA_NOTE: Record<TseZoneAreaMethod, string> = {
  'municipios-ibge': 'Área = municípios inteiros (malha oficial IBGE).',
  'setores-ibge-2022':
    'Área aproximada: setores censitários IBGE 2022 atribuídos ao local de votação mais próximo (o TSE não publica o contorno das zonas).',
};

/** Rotulo curto da zona, ex.: "62ª Zona Eleitoral". */
export function tseZoneTitle(nrZona: number): string {
  return `${nrZona}ª Zona Eleitoral`;
}

/** Linha de um candidato (posicao, nome, partido colorido, %). */
function tseCandidatoRow(posicao: number, candidato: TseZoneCandidato): string {
  const partido = escapeHtml(candidato.partido);
  return (
    `<div class="exp-vp-row"><span class="exp-vp-pos">${posicao}º</span>` +
    `<b class="exp-vp-name">${escapeHtml(candidato.nome)}</b> ` +
    `<span class="exp-vp-party" style="color:${bubbleColorOf(candidato.partido)}">(${partido})</span>` +
    `<span class="exp-vp-num">${formatPercent(candidato.pct)}</span></div>`
  );
}

/** Bloco vencedor + 2o de um ranking; vazio vira "sem votos apurados". */
function tseRankingRows(ranking: TseZoneRanking): string {
  if (!ranking.vencedor) return '<div class="exp-vp-row">Sem votos apurados</div>';
  const segundo = ranking.segundo ? tseCandidatoRow(2, ranking.segundo) : '';
  return tseCandidatoRow(1, ranking.vencedor) + segundo;
}

/** Secao de Prefeito 2024: um sub-bloco por municipio quando a zona tem mais de um. */
function tsePrefeitoSection(props: TseZoneProperties): string {
  const multi = props.prefeito2024.length > 1;
  const blocks = props.prefeito2024.map((result) => {
    const label = multi ? `<div class="exp-tz-mun">${escapeHtml(result.municipio)}</div>` : '';
    return label + tseRankingRows(result);
  });
  return `<div class="exp-vp-title exp-tz-title">Prefeito 2024 · 1º turno</div>${blocks.join('')}`;
}

/**
 * HTML completo do popup da zona.
 *
 * @example buildTseZonePopupHtml(feature.properties) // '<div class="exp-vp-head">...'
 */
export function buildTseZonePopupHtml(props: TseZoneProperties): string {
  const municipios = props.municipios.map(escapeHtml).join(' + ');
  const head =
    `<div class="exp-vp-head"><b>${tseZoneTitle(props.nrZona)}</b> · ${municipios}<br>` +
    `<b>${formatVotos(props.eleitores)}</b> eleitores · ${props.locais} locais de votação (TSE 2024)</div>`;
  const governador = `<div class="exp-vp-title exp-tz-title">Governador 2022 · 1º turno</div>${tseRankingRows(props.governador2022)}`;
  const note = `<div class="exp-tz-note">${TSE_ZONE_AREA_NOTE[props.metodo]}</div>`;
  return `${head}${tsePrefeitoSection(props)}${governador}${note}`;
}
